import type { ICheckoutContext } from '@/types/context';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { stripeService } from '@/services/stripe';
import { buildStripeApplePayRequest } from '@/utils/applePay';
import { buildAdyenApplePayPaymentRequest } from '@/utils/adyen';
import { Errors } from '@/constants/errors';
import type {
  ApplePayPaymentParams,
  AdyenApplePayPaymentParams,
  ChargebeeWalletPaymentParams,
  StripeExpressCheckoutParams,
  NuveiApplePayPaymentParams,
} from '@/types/paymentHandlers';
import { finalizeChargebeeWalletPayment } from '@/payment/chargebeePaymentHandler';
import type { AdyenPaymentResponse, StripePaymentResponse, NuveiPaymentResponse } from '@/types/payment';
import { logError } from '@/utils/logger';
import { buildNuveiWalletPaymentRequest } from '@/utils/nuvei';

const handleStripeApplePay = async (
  _checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params: StripeExpressCheckoutParams,
  providerSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<StripePaymentResponse | null> => {
  const { event, elements, stripe } = params;

  const billingDetails = {
    ...event.billingDetails,
    address: {
      ...event.billingDetails?.address,
      line2: event.billingDetails?.address?.line2 || '',
    },
  };

  // `stripe` is the instance that owns `elements` — see
  // `StripeExpressCheckoutParams.stripe` for why the shared slot is not enough.
  const applePayResults = await stripeService.createApplePayPaymentMethod(elements, billingDetails, stripe);
  if (applePayResults) {
    return (await sendPaymentToServer(
      buildStripeApplePayRequest(applePayResults, providerSettings, event.billingDetails!, params.zipCode)
    )) as StripePaymentResponse;
  }
  return null;
};

const handleAdyenApplePay = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params: AdyenApplePayPaymentParams,
  providerSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<AdyenPaymentResponse> => {
  const request = buildAdyenApplePayPaymentRequest(params, providerSettings, checkout.customer);
  return (await sendPaymentToServer(request)) as AdyenPaymentResponse;
};

const handleNuveiApplePay = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params: NuveiApplePayPaymentParams,
  providerSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<NuveiPaymentResponse> => {
  const request = buildNuveiWalletPaymentRequest({
    paymentMethodId: providerSettings.id || '',
    paymentMethodDisplayName: providerSettings.paymentMethodDisplayName || 'Apple Pay',
    // Sheet-collected email wins — same precedence as the Adyen wallet handlers.
    email: params.email || checkout.customer.email || '',
    sessionToken: params.sessionToken,
    walletParams: { applePay: { walletIssuedEncryptedToken: params.walletIssuedEncryptedToken } },
  });
  return (await sendPaymentToServer(request)) as NuveiPaymentResponse;
};

export const handleApplePayPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params?: ApplePayPaymentParams
): Promise<StripePaymentResponse | AdyenPaymentResponse | NuveiPaymentResponse | null> => {
  if (!params) {
    throw new Error('Apple Pay payment requires parameters');
  }

  const providerSettings = checkout.paymentMethodsSettings.find(
    (pm) => pm.paymentMethodName === PaymentMethodEnum.APPLE_PAY
  );

  if (!providerSettings) {
    throw new Error('Apple Pay payment method settings not found');
  }

  try {
    switch (providerSettings.provider) {
      case ProviderEnum.STRIPE:
        return await handleStripeApplePay(
          checkout,
          sendPaymentToServer,
          params as StripeExpressCheckoutParams,
          providerSettings
        );
      case ProviderEnum.ADYEN:
        return await handleAdyenApplePay(
          checkout,
          sendPaymentToServer,
          params as AdyenApplePayPaymentParams,
          providerSettings
        );
      case ProviderEnum.NUVEI:
        return await handleNuveiApplePay(
          checkout,
          sendPaymentToServer,
          params as NuveiApplePayPaymentParams,
          providerSettings
        );
      // Subscriptions: Chargebee's mounted button already ran the sheet and
      // authorized the intent — only the server-side finalize remains.
      case ProviderEnum.CHARGEBEE:
        return (await finalizeChargebeeWalletPayment(
          checkout,
          sendPaymentToServer,
          (params as ChargebeeWalletPaymentParams).paymentIntent
        )) as StripePaymentResponse;
      default:
        throw new Error(`Unsupported provider for Apple Pay: ${providerSettings.provider}`);
    }
  } catch (error: unknown) {
    logError(Errors.apple.errorWhileSendingPayment, error);
    throw error;
  }
};
